import { Component, Watch } from 'vue-property-decorator';
import AbstractView from '@/views/abstract-view';
import EventBus from '@vertical-plus/vue-js-core/dist/util/event/event-bus';
import FormFieldValuesInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-values-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';
import InvoiceIndex from '@/components/invoice-index/index.vue';
import JobIndex from '@/components/job-index/index.vue';
import Techspec from '@/model/entity/job/techspec';
import TechspecDuplicateForm from '@/components/techspec-duplicate-form/index.vue';
import TechspecMilestones from '@/components/techspec-milestones/index.vue';

@Component({
    components: {
        'app-invoice-index': InvoiceIndex,
        'app-job-index': JobIndex,
        'app-techspec-duplicate-form': TechspecDuplicateForm,
        'app-techspec-milestones': TechspecMilestones,
    },
})
export default class TechspecView extends AbstractView
{
    private techspec: Techspec|null = null;
    private tab = 'jobs';
    private options = false;
    private duplicationDialog = false;

    private form: FormInterface|null = null;

    protected get title(): string
    {
        return this.techspec?.name ?? this.defaultTitle;
    }

    protected created()
    {
        this.loadTechspec();
    }

    @Watch('$route')
    private onRouteChanged()
    {
        this.loadTechspec();
    }

    /**
     * Generates the quick edit form
     *
     * @returns
     */

    private generateQuickEditForm()
    {
        if (!this.techspec)
        {
            return;
        }

        const form: FormInterface =
        {
            items: [
                {
                    key: 'deadline',
                    type: 'datetime',
                    title: this.$t('general.field.deadline').toString(),
                    value: this.techspec.deadline,
                    required: false,
                    clearable: true,
                },
                {
                    key: 'manager',
                    type: 'string',
                    title: this.$t('general.field.manager').toString(),
                    value: this.techspec.managerId as string,
                    required: true,
                    optionsCallback: async(search: string) => this.$serviceContainer.userService.formOptionsCallback(search),
                    titlesCallback: async(values: string[]) => this.$serviceContainer.userService.formTitlesCallback(values),
                },
            ],
        };

        this.form = Object.assign({}, form);
    }

    /**
     * Loads the techspec
     */

    private async loadTechspec()
    {
        try
        {
            this.techspec = await this.$serviceContainer.techspecService.loadTechspec(this.$route.params.id);

            this.generateQuickEditForm();
        }
        catch (error: unknown)
        {
            EventBus.instance.$emit(
                'notification',
                this.$t('general.error.failed_loading_server_error'),
                'error',
            );
        }
    }

    /**
     * Handles quick edit value changes
     */

    private async onQuickEditValuesChanged(values: FormFieldValuesInterface)
    {
        if (!this.techspec)
        {
            return;
        }

        let changed = false;

        // Deadline changed
        if (!values.deadline && this.techspec.deadline)
        {
            this.techspec.deadline = null;
            changed = true;
        }
        else if (values.deadline && (values.deadline as Date).getTime() !== this.techspec.deadline?.getTime())
        {
            this.techspec.deadline = values.deadline as Date;
            changed = true;
        }

        // Manager changed
        if (values.manager !== this.techspec.managerId)
        {
            this.techspec.managerId = values.manager as string;
            changed = true;
        }

        if (changed)
        {
            try
            {
                await this.$serviceContainer.techspecService.updateTechspec(this.techspec);

                EventBus.instance.$emit(
                    'notification',
                    this.$t('general.message.saved_successfully'),
                    'success',
                );
            }
            catch (error: unknown)
            {
                EventBus.instance.$emit(
                    'notification',
                    this.$t('general.error.failed_saving_server_error'),
                    'error',
                );
            }
        }
    }

    /**
     * Handles duplication click events
     */

    private onDuplicate()
    {
        this.duplicationDialog = true;
    }

    /**
     * Handles duplication cancellation
     */

    private onCancelDuplicateTechspec()
    {
        this.duplicationDialog = false;
    }

    /**
     * Handles duplications
     *
     * @param duplicateId
     */

    private onDuplicated(duplicateId: number)
    {
        this.duplicationDialog = false;

        this.$router.push({
            name: 'techspec-view',
            params: {
                id: duplicateId.toString(),
            },
        }).catch();
    }
}